import React, { useEffect, useState } from "react";

const buildLocalState = (offer) => ({
  docNumber: offer?.docNumber || "",
  date: offer?.date || new Date().toISOString().slice(0, 10),
  currency: offer?.currency || "RUB",
  title: offer?.title || "",
  company: offer?.company || "",
  description: offer?.description || "",
  clientName: offer?.client?.name || "",
  clientPhone: offer?.client?.phone || "",
  clientEmail: offer?.client?.email || "",
  clientAddress: offer?.client?.address || "",
  delivery: offer?.meta?.delivery || "",
  payment: offer?.meta?.payment || "",
  warranty: offer?.meta?.warranty || "",
  validity: offer?.meta?.validity || "",
  extra: offer?.meta?.extra || "",
  documentHeading:
    offer?.meta?.documentHeading || "КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ",
  documentDateText: offer?.meta?.documentDateText || "",
  showRecipient: Boolean(offer?.meta?.showRecipient),
  recipientLabel: offer?.meta?.recipientLabel || "Кому",
  recipientText: offer?.meta?.recipientText || "",
  showSender: Boolean(offer?.meta?.showSender),
  senderLabel: offer?.meta?.senderLabel || "От",
  senderText: offer?.meta?.senderText || "",
  showSignature: Boolean(offer?.meta?.showSignature),
  signatureLabel: offer?.meta?.signatureLabel || "Подпись",
  signaturePosition: offer?.meta?.signaturePosition || "",
  signatureName: offer?.meta?.signatureName || "",
});

const Field = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
  type = "text",
  placeholder = "",
  className = "",
}) => (
  <label className={`offer-header__field ${className}`.trim()}>
    <span>{label}</span>
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
    />
  </label>
);

const TextAreaField = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
  placeholder = "",
  rows = 4,
  className = "",
}) => (
  <label
    className={`offer-header__field offer-header__field--textarea ${className}`.trim()}
  >
    <span>{label}</span>
    <textarea
      rows={rows}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
    />
  </label>
);

const SelectField = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
  options,
}) => (
  <label className="offer-header__field">
    <span>{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const ToggleField = ({ label, description, checked, onChange }) => (
  <label className="offer-header__toggle">
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="offer-header__toggle-control" aria-hidden="true" />
    <span className="offer-header__toggle-copy">
      <strong>{label}</strong>
      {description && <small>{description}</small>}
    </span>
  </label>
);

const OfferHeaderForm = ({
  offer,
  locking,
  onChange,
  onLock,
  onUnlock,
}) => {
  const [local, setLocal] = useState(() => buildLocalState(offer));

  useEffect(() => {
    setLocal(buildLocalState(offer));
  }, [
    offer?._id,
    offer?.docNumber,
    offer?.date,
    offer?.currency,
    offer?.title,
    offer?.company,
    offer?.description,
    offer?.client?.name,
    offer?.client?.phone,
    offer?.client?.email,
    offer?.client?.address,
    offer?.meta?.delivery,
    offer?.meta?.payment,
    offer?.meta?.warranty,
    offer?.meta?.validity,
    offer?.meta?.extra,
    offer?.meta?.documentHeading,
    offer?.meta?.documentDateText,
    offer?.meta?.showRecipient,
    offer?.meta?.recipientLabel,
    offer?.meta?.recipientText,
    offer?.meta?.showSender,
    offer?.meta?.senderLabel,
    offer?.meta?.senderText,
    offer?.meta?.showSignature,
    offer?.meta?.signatureLabel,
    offer?.meta?.signaturePosition,
    offer?.meta?.signatureName,
  ]);

  const isLocked = (path) => Boolean(locking?.[path]);

  const handle = (key, value) => {
    setLocal((previous) => ({ ...previous, [key]: value }));
  };

  const buildPatch = (state) => ({
    docNumber: state.docNumber.trim(),
    date: state.date,
    currency: state.currency,
    title: state.title.trim(),
    company: state.company.trim(),
    description: state.description,
    client: {
      ...(offer.client || {}),
      name: state.clientName.trim(),
      phone: state.clientPhone.trim(),
      email: state.clientEmail.trim(),
      address: state.clientAddress.trim(),
    },
    meta: {
      ...(offer.meta || {}),
      delivery: state.delivery,
      payment: state.payment,
      warranty: state.warranty,
      validity: state.validity,
      extra: state.extra,
      documentHeading: state.documentHeading.trim(),
      documentDateText: state.documentDateText.trim(),
      showRecipient: Boolean(state.showRecipient),
      recipientLabel: state.recipientLabel.trim(),
      recipientText: state.recipientText,
      showSender: Boolean(state.showSender),
      senderLabel: state.senderLabel.trim(),
      senderText: state.senderText,
      showSignature: Boolean(state.showSignature),
      signatureLabel: state.signatureLabel.trim(),
      signaturePosition: state.signaturePosition.trim(),
      signatureName: state.signatureName.trim(),
    },
  });

  const commit = async (state = local) => {
    await onChange(buildPatch(state));
  };

  const updateBoolean = async (key, value) => {
    const next = { ...local, [key]: Boolean(value) };
    setLocal(next);
    await commit(next);
  };

  const fieldEvents = (path) => ({
    onFocus: () => onLock(path),
    onBlur: async () => {
      onUnlock(path);
      await commit();
    },
    disabled: isLocked(path),
  });

  return (
    <div className="offer-header">
      <div className="offer-section-heading">
        <div>
          <span className="offer-section-heading__index">01</span>
          <div>
            <h3>Документ</h3>
            <p>Название, номер, дата и формулировка заголовка.</p>
          </div>
        </div>
      </div>

      <div className="offer-header__grid offer-header__grid--document">
        <Field
          label="Номер КП"
          value={local.docNumber}
          onChange={(value) => handle("docNumber", value)}
          placeholder="КП-2026-001"
          {...fieldEvents("docNumber")}
        />

        <Field
          label="Дата документа"
          type="date"
          value={local.date}
          onChange={(value) => handle("date", value)}
          {...fieldEvents("date")}
        />

        <SelectField
          label="Валюта"
          value={local.currency}
          onChange={(value) => handle("currency", value)}
          options={[
            { value: "RUB", label: "Российский рубль (₽)" },
            { value: "EUR", label: "Евро (€)" },
            { value: "USD", label: "Доллар США ($)" },
          ]}
          {...fieldEvents("currency")}
        />

        <Field
          label="Заголовок документа"
          value={local.documentHeading}
          onChange={(value) => handle("documentHeading", value)}
          placeholder="КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ"
          className="offer-header__field--wide"
          {...fieldEvents("meta.documentHeading")}
        />

        <Field
          label="Дата в заголовке"
          value={local.documentDateText}
          onChange={(value) => handle("documentDateText", value)}
          placeholder="от «15» июля 2026 года"
          {...fieldEvents("meta.documentDateText")}
        />

        <Field
          label="Название предложения"
          value={local.title}
          onChange={(value) => handle("title", value)}
          placeholder="Изготовление металлического секционного ограждения"
          className="offer-header__field--wide"
          {...fieldEvents("title")}
        />

        <Field
          label="Исполнитель / компания"
          value={local.company}
          onChange={(value) => handle("company", value)}
          placeholder="ИП Маслак Алексей Сергеевич"
          {...fieldEvents("company")}
        />

        <TextAreaField
          label="Краткое описание"
          value={local.description}
          onChange={(value) => handle("description", value)}
          placeholder="Кратко опишите предмет предложения и ключевой результат для заказчика."
          rows={4}
          className="offer-header__field--full"
          {...fieldEvents("description")}
        />
      </div>

      <div className="offer-section-heading">
        <div>
          <span className="offer-section-heading__index">02</span>
          <div>
            <h3>Кому и от кого</h3>
            <p>Каждый блок независимо включается или скрывается в PDF.</p>
          </div>
        </div>
      </div>

      <div className="offer-header__visibility-grid">
        <div className="offer-header__visibility-card">
          <ToggleField
            label="Показывать блок «Кому»"
            description="Адресат появится в верхней части документа."
            checked={local.showRecipient}
            onChange={(value) => updateBoolean("showRecipient", value)}
          />
          <div className="offer-header__grid offer-header__grid--addressing">
            <Field
              label="Подпись поля"
              value={local.recipientLabel}
              onChange={(value) => handle("recipientLabel", value)}
              placeholder="Кому"
              {...fieldEvents("meta.recipientLabel")}
            />
            <TextAreaField
              label="Кому"
              value={local.recipientText}
              onChange={(value) => handle("recipientText", value)}
              placeholder="МКУ «УЖКХ Ловозерского муниципального округа»"
              rows={3}
              className="offer-header__field--wide"
              {...fieldEvents("meta.recipientText")}
            />
          </div>
        </div>

        <div className="offer-header__visibility-card">
          <ToggleField
            label="Показывать блок «От кого»"
            description="Исполнитель и его контакты появятся рядом с адресатом."
            checked={local.showSender}
            onChange={(value) => updateBoolean("showSender", value)}
          />
          <div className="offer-header__grid offer-header__grid--addressing">
            <Field
              label="Подпись поля"
              value={local.senderLabel}
              onChange={(value) => handle("senderLabel", value)}
              placeholder="От"
              {...fieldEvents("meta.senderLabel")}
            />
            <TextAreaField
              label="От кого"
              value={local.senderText}
              onChange={(value) => handle("senderText", value)}
              placeholder={
                "ИП Маслак Алексей Сергеевич\nконтактный номер +79508972270"
              }
              rows={3}
              className="offer-header__field--wide"
              {...fieldEvents("meta.senderText")}
            />
          </div>
        </div>
      </div>

      <div className="offer-section-heading">
        <div>
          <span className="offer-section-heading__index">03</span>
          <div>
            <h3>Заказчик</h3>
            <p>Контактные данные для отдельного блока заказчика.</p>
          </div>
        </div>
      </div>

      <div className="offer-header__grid">
        <Field
          label="Имя или организация"
          value={local.clientName}
          onChange={(value) => handle("clientName", value)}
          placeholder="Наименование заказчика"
          {...fieldEvents("client.name")}
        />

        <Field
          label="Телефон"
          value={local.clientPhone}
          onChange={(value) => handle("clientPhone", value)}
          placeholder="+7 (___) ___-__-__"
          {...fieldEvents("client.phone")}
        />

        <Field
          label="Email"
          type="email"
          value={local.clientEmail}
          onChange={(value) => handle("clientEmail", value)}
          placeholder="client@example.ru"
          {...fieldEvents("client.email")}
        />

        <Field
          label="Адрес"
          value={local.clientAddress}
          onChange={(value) => handle("clientAddress", value)}
          placeholder="Город, адрес объекта"
          className="offer-header__field--full"
          {...fieldEvents("client.address")}
        />
      </div>

      <div className="offer-section-heading">
        <div>
          <span className="offer-section-heading__index">04</span>
          <div>
            <h3>Условия</h3>
            <p>Эти данные выводятся отдельным блоком в итоговом PDF.</p>
          </div>
        </div>
      </div>

      <div className="offer-header__grid offer-header__grid--conditions">
        <TextAreaField
          label="Срок изготовления / поставки"
          value={local.delivery}
          onChange={(value) => handle("delivery", value)}
          placeholder="30 календарных дней с даты заключения договора"
          rows={3}
          {...fieldEvents("meta.delivery")}
        />

        <TextAreaField
          label="Условия оплаты"
          value={local.payment}
          onChange={(value) => handle("payment", value)}
          placeholder="50% предоплата, 50% после готовности"
          rows={3}
          {...fieldEvents("meta.payment")}
        />

        <Field
          label="Гарантия"
          value={local.warranty}
          onChange={(value) => handle("warranty", value)}
          placeholder="1 год"
          {...fieldEvents("meta.warranty")}
        />

        <Field
          label="Срок действия предложения"
          value={local.validity}
          onChange={(value) => handle("validity", value)}
          placeholder="14 календарных дней"
          {...fieldEvents("meta.validity")}
        />

        <TextAreaField
          label="Дополнительные условия"
          value={local.extra}
          onChange={(value) => handle("extra", value)}
          placeholder="Например: доставка и монтаж рассчитываются отдельно."
          rows={4}
          className="offer-header__field--full"
          {...fieldEvents("meta.extra")}
        />
      </div>

      <div className="offer-section-heading">
        <div>
          <span className="offer-section-heading__index">05</span>
          <div>
            <h3>Подпись</h3>
            <p>Оставляет в PDF свободное место для рукописной подписи.</p>
          </div>
        </div>
      </div>

      <div className="offer-header__signature-settings">
        <ToggleField
          label="Добавить место для подписи"
          description="Блок будет выведен в нижней части предложения."
          checked={local.showSignature}
          onChange={(value) => updateBoolean("showSignature", value)}
        />

        <div className="offer-header__grid">
          <Field
            label="Подпись поля"
            value={local.signatureLabel}
            onChange={(value) => handle("signatureLabel", value)}
            placeholder="Подпись"
            {...fieldEvents("meta.signatureLabel")}
          />

          <Field
            label="Должность / статус"
            value={local.signaturePosition}
            onChange={(value) => handle("signaturePosition", value)}
            placeholder="Индивидуальный предприниматель"
            {...fieldEvents("meta.signaturePosition")}
          />

          <Field
            label="ФИО"
            value={local.signatureName}
            onChange={(value) => handle("signatureName", value)}
            placeholder="Маслак Алексей Сергеевич"
            {...fieldEvents("meta.signatureName")}
          />
        </div>
      </div>
    </div>
  );
};

export default OfferHeaderForm;
